import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Card,
  Row,
  Col,
  Modal,
  message,
  Space,
  Typography,
  Tooltip,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { apiGetRoles, apiDeleteRole } from '@services/ManageRoles/RoleServices';
import { showSuccess, showError } from '@components/notification';
import RoleFormModal from './RoleFormModal';
import AssignPermissionsModal from './AssignPermissionsModal';
import { usePermission } from '@contexts/Auth/UsePermission';

const { Title } = Typography;

export default function ManageRoles() {
  const { userManagement } = usePermission();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiGetRoles({
        page: currentPage,
        size: pageSize,
        keyword: keyword || undefined
      });

      if (response?.data?.success !== false) {
        const roles = response.data?.data || [];
        setData(
          roles.map((item, index) => ({
            ...item,
            key: item.id,
            stt: (currentPage - 1) * pageSize + index + 1
          }))
        );
        setTotalItems(response.data?.meta?.total || 0);
      } else {
        setData([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Lỗi khi tải danh sách roles:', error);
      showError(error?.message || 'Không thể tải danh sách vai trò');
      setData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchData();
  };

  const handleDelete = async (id) => {
    try {
      await apiDeleteRole(id);
      showSuccess({ message: 'Xóa vai trò thành công!' });
      fetchData();
    } catch (error) {
      showError(error?.message || 'Xóa vai trò thất bại');
    }
  };

  const handleEdit = (record) => {
    setEditingRole(record);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingRole(null);
    setIsModalOpen(true);
  };

  const handleAssignPermissions = (record) => {
    setSelectedRole(record);
    setIsPermissionModalOpen(true);
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'stt',
      width: 70,
      align: 'center'
    },
    {
      title: 'Tên vai trò',
      dataIndex: 'name',
      render: (text) => (
        <Typography.Text strong style={{ color: '#0051A9' }}>
          {text}
        </Typography.Text>
      )
    },
    {
      title: 'Mô tả',
      dataIndex: 'descriptions',
      render: (text) => text || 'Không có mô tả'
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      width: 150,
      render: (text) => (text ? new Date(text).toLocaleString('vi-VN') : '')
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 200,
      align: 'center',
      render: (_, record) => (
        <Space>
          {userManagement.manageRoles && (
            <Tooltip title="Gán quyền">
              <Button
                type="primary"
                icon={<SettingOutlined />}
                size="small"
                onClick={() => handleAssignPermissions(record)}
                style={{
                  marginRight: 8,
                  backgroundColor: '#52c41a',
                  borderColor: '#52c41a'
                }}
              />
            </Tooltip>
          )}
          {userManagement.manageRoles && (
            <Tooltip title="Chỉnh sửa">
              <Button
                type="primary"
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEdit(record)}
                style={{
                  marginRight: 8,
                  backgroundColor: '#1890ff',
                  borderColor: '#1890ff'
                }}
              />
            </Tooltip>
          )}
          {userManagement.manageRoles && (
            <Tooltip title="Xóa">
              <Popconfirm
                title="Bạn có chắc chắn muốn xóa vai trò này?"
                onConfirm={() => handleDelete(record.id)}
                okText="Xóa"
                cancelText="Hủy">
                <Button danger icon={<DeleteOutlined />} size="small" style={{ marginRight: 8 }} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ color: '#0051A9', margin: 0 }}>
            Quản lý vai trò
          </Title>
        </Col>
        <Col>
          {userManagement.manageRoles && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{ backgroundColor: '#0051A9', borderColor: '#0051A9' }}>
              Thêm vai trò
            </Button>
          )}
        </Col>
      </Row>

      <Card bodyStyle={{ padding: '8px' }}>
        <Row gutter={[16, 16]}>
          <Col flex="auto">
            <Input
              placeholder="Tìm kiếm theo tên vai trò..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              style={{ backgroundColor: '#0051A9', borderColor: '#0051A9' }}>
              Tìm kiếm
            </Button>
          </Col>
        </Row>
      </Card>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          showSizeChanger: true,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
        }}
        bordered
        scroll={{ x: 'max-content' }}
      />

      <RoleFormModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingRole(null);
        }}
        editingRole={editingRole}
        onSuccess={fetchData}
      />

      <AssignPermissionsModal
        open={isPermissionModalOpen}
        onClose={() => {
          setIsPermissionModalOpen(false);
          setSelectedRole(null);
        }}
        role={selectedRole}
      />
    </div>
  );
}
